/**
 * AdminBulkActionBar — sticky floating action bar that surfaces the
 * 3-button report pipeline actions (Download Missing / Analyse
 * Downloaded / Download & Analyse All) for a set of admin-selected
 * stocks.
 *
 * Used by the Stock Screener (`/app/frontend/src/pages/StocksPage.js`)
 * — the admin ticks checkboxes on the screener rows, and this bar
 * fades in at the bottom of the viewport with the full batch action
 * controls. Mirrors the single-stock control rendered on the Stock
 * Detail reports tab and the bulk picker on the Reports admin page.
 *
 * Props:
 *   companyCodes : string[]   — company_codes (e.g. "COMB", "HNB") the bar acts on.
 *   displayLabels: string[]?  — optional tickers to show as chips (e.g. "COMB.N0000").
 *                               Falls back to companyCodes when omitted.
 *   onClear      : () => void — called when the admin hits "Clear selection"
 *                               OR after a successful action (so the UI
 *                               empties back out automatically).
 *   reportTypes  : string[]?  — default ['annual']. `null` = admin policy.
 *   yearsToKeep  : number?    — default 5. `0` or null = unlimited.
 *   limitPerCompany : number? — default 10.
 *   analysisLimit   : number? — default 60.
 *
 * IMPORTANT — admin-only. The parent is responsible for gating render
 * behind `isAdmin`; the component itself performs no auth check.
 */
import React, { useState } from "react";
import { apiPost } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CloudDownload, Sparkles, Loader2, X } from "lucide-react";

const AdminBulkActionBar = ({
  companyCodes = [],
  displayLabels = null,
  onClear,
  reportTypes = ["annual"],
  yearsToKeep = 5,
  limitPerCompany = 10,
  analysisLimit = 60,
  testIdPrefix = "admin-bulk-bar",
}) => {
  const [busy, setBusy] = useState(null); // "download" | "analyse" | "both" | null

  if (!companyCodes || companyCodes.length === 0) return null;

  const labels = displayLabels && displayLabels.length ? displayLabels : companyCodes;
  // Show up to 6 chips then a "+N more" so the bar stays compact.
  const CHIP_CAP = 6;
  const visibleChips = labels.slice(0, CHIP_CAP);
  const overflow = labels.length - visibleChips.length;

  const runAction = async (mode) => {
    const actionMap = { download: "download", analyse: "analyse", both: "download_and_analyse" };
    const action = actionMap[mode];
    setBusy(mode);
    // Immediate progress toast — the actual API call below resolves
    // after CSE fetches synchronously (~1s per company for download,
    // instant for analyse queueing).
    const progressToastId = toast.loading(
      mode === "download"
        ? `Downloading reports for ${companyCodes.length} stock${companyCodes.length === 1 ? "" : "s"}…`
        : mode === "analyse"
          ? `Queuing analysis for ${companyCodes.length} stock${companyCodes.length === 1 ? "" : "s"}…`
          : `Downloading + analysing for ${companyCodes.length} stock${companyCodes.length === 1 ? "" : "s"}…`,
      { duration: 20_000 }
    );
    try {
      const res = await apiPost("/admin/reports/bulk-action", {
        action,
        company_codes: companyCodes,
        report_types: reportTypes,
        years_to_keep: yearsToKeep,
        limit_per_company: limitPerCompany,
        analysis_limit: analysisLimit,
      });
      const downloaded = res?.downloaded ?? 0;
      const skipped = res?.skipped_existing ?? 0;
      const queued = res?.analysis_queued ?? 0;
      const errors = res?.download_errors ?? 0;
      const estUsd = res?.analysis_estimate?.total_usd;
      toast.dismiss(progressToastId);
      if (mode === "download") {
        toast.success(
          downloaded > 0
            ? `Downloaded ${downloaded} new report${downloaded === 1 ? "" : "s"} for ${companyCodes.length} stock${companyCodes.length === 1 ? "" : "s"}`
            : "No new reports found on cse.lk",
          {
            description: [
              skipped ? `${skipped} already on file.` : null,
              errors ? `${errors} download error${errors === 1 ? "" : "s"}.` : null,
              "Analysis NOT triggered — click ‘Analyse Downloaded Only’ to run Claude.",
            ].filter(Boolean).join(" "),
            duration: 7000,
          }
        );
      } else if (mode === "analyse") {
        toast.success(
          queued > 0
            ? `Queuing analysis for ${companyCodes.length} stock${companyCodes.length === 1 ? "" : "s"} (~${queued} report${queued === 1 ? "" : "s"}${estUsd ? `, est. $${estUsd.toFixed(2)}` : ""})`
            : "No pending reports to analyse",
          {
            description: queued > 0
              ? "Claude is running in the background. Refresh the Reports library in ~1–2 min."
              : "Every report on file for the selected stocks is already analysed.",
            duration: 7000,
          }
        );
      } else {
        toast.success(
          `Downloaded ${downloaded} new report${downloaded === 1 ? "" : "s"} for ${companyCodes.length} stock${companyCodes.length === 1 ? "" : "s"}. Analysis queued for ${queued}.`,
          {
            description: estUsd ? `Estimated Claude spend: $${estUsd.toFixed(2)}. Running in the background.` : "Claude is running in the background.",
            duration: 7000,
          }
        );
      }
      // Auto-clear selection so the bar disappears after a successful run.
      if (typeof onClear === "function") onClear();
    } catch (e) {
      toast.dismiss(progressToastId);
      toast.error(e?.response?.data?.detail || `Bulk ${mode} failed`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[min(980px,calc(100vw-2rem))]"
      data-testid={`${testIdPrefix}-container`}
    >
      <div className="rounded-xl border bg-card/95 backdrop-blur-md shadow-lg ring-1 ring-black/5 px-4 py-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          {/* Left — count + chips */}
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <Badge variant="default" className="font-semibold" data-testid={`${testIdPrefix}-count`}>
              {companyCodes.length} stock{companyCodes.length === 1 ? "" : "s"} selected
            </Badge>
            <div className="flex items-center gap-1 flex-wrap">
              {visibleChips.map((label) => (
                <Badge
                  key={label}
                  variant="outline"
                  className="font-mono text-[10px]"
                  data-testid={`${testIdPrefix}-chip-${label}`}
                >
                  {label}
                </Badge>
              ))}
              {overflow > 0 && (
                <Badge variant="secondary" className="text-[10px]">
                  +{overflow} more
                </Badge>
              )}
            </div>
          </div>
          {/* Right — 3 actions + clear */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={() => runAction("download")}
              disabled={!!busy}
              className="gap-1.5"
              data-testid={`${testIdPrefix}-download-button`}
              title="Fetch new reports from cse.lk for the selected stocks. NO Claude analysis."
            >
              {busy === "download" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CloudDownload className="h-3.5 w-3.5" />}
              Download Missing
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => runAction("analyse")}
              disabled={!!busy}
              className="gap-1.5"
              data-testid={`${testIdPrefix}-analyse-button`}
              title="Run Claude on reports already on file (pending analysis). NO new downloads."
            >
              {busy === "analyse" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Analyse Downloaded
            </Button>
            <Button
              size="sm"
              variant="default"
              onClick={() => runAction("both")}
              disabled={!!busy}
              className="gap-1.5"
              data-testid={`${testIdPrefix}-both-button`}
              title="Fetch new reports AND immediately run Claude on them."
            >
              {busy === "both" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
                <span className="inline-flex items-center gap-0.5">
                  <CloudDownload className="h-3.5 w-3.5" />
                  <Sparkles className="h-3.5 w-3.5" />
                </span>
              )}
              Download & Analyse All
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { if (typeof onClear === "function") onClear(); }}
              disabled={!!busy}
              className="gap-1.5"
              data-testid={`${testIdPrefix}-clear-button`}
              title="Clear the selection"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBulkActionBar;
